import * as actionTypes from "../mutation-types";
import SubscriberDetailsProvider from "../../providers/idp/SubscriberDetailsProvider";
import SubscriptionProvider from "../../providers/idp/SubscriptionProvider";
import AppUsersQueryProvider from "../../providers/AppUsersQueryProvider";
import VouchersProvider from "../../providers/VouchersProvider";
import FavoriteRestaurantProvider from "../../providers/FavoriteRestaurantProvider";
import AppUsersProvider from "../../providers/AppUsersProvider";
import { refreshUserToken } from "../../helper/IdpHelper";
import appConfig from "../../appconfig";
import * as mutationTypes from "../mutation-types";
import FavoriteProductProvider from "../../providers/FavoriteProductProvider";
import RecommendationEngineProvider from "../../providers/RecommendationEngineProvider";
import { evaluateCustomerType } from "../../libraries/utils";
import LimitedDiscountAssignmentsApi from "../../providers/LimitedDiscountAssignmentsApi";

const initialState = () => ({
	userIdpToken: null,
	userRefreshToken: null,
	customerDetails: null,
	favoriteProducts: [],
	favoriteRestaurants: [],
	customerExists: false,
	customerFetchingSuccess: true,
	isSubscribeFlow: false,
	subscription: {
		id: null,
		type: null,
		requiresCVR: false
	},
	userCvrNumber: null,
	isSubscribeSubFlow: false,
	isShowDevSupport: false,
	isUserOrdersSubFlow: false,
	isFeedbackSubFlow: false,
	isUserDataSubFlow: false,
	feedbackLeft: false,
	focusSubscriptionTerms: false,
	submitSubscriptionTerms: false,
	subscriptionPaymentMethod: null,
	fraudProtection: {
		modalVisible: false,
		activeSessionsNumber: undefined,
		maximumDevicesNumber: undefined,
		activeSessions: undefined,
		messageType: undefined,
		browserOsInfo: undefined
	},
	idpAction: "",
	savingsStatistics: {
		allTime: undefined,
		specificTimeSpan: undefined
	},
	subscriptionExistsData: {},
	pushNotificationsGranted: false,
	historyProducts: [],
	recurringPaymentDetails: {},
	testUsers: [],
	userCrmVouchersAndMilestoneRewards: [],
	rcmCatalogPagesSortingData: null,
	subscriptionDetailsIsAlreadyDowngraded: false,
	subscriptionUpgradePlanName: null,
	subscriptionDowngradePlanName: null,
	lastRenderedRouteSideMenu: null,
	subscriptionUpgradePackage: null,
	subscriptionMembershipPlans: [],
	limitedDiscountAssignments: null
});

// initial state
const state = {
	persistedData: initialState(),
	userOrders: [],
	userLastOrder: null,
	uniqueSessionId: null,
	auth: {
		isRefreshing: false,
		refreshingCall: undefined
	},
	forceShowingSubscriptionPlans: false
};

// getters
const getters = {
	applicationUserIdpToken: state => state.persistedData.userIdpToken,
	applicationUserData: state => state.persistedData.customerDetails,
	applicationUserLoggedIn: state => state.persistedData.userIdpToken !== null,
	customerExists: state => state.persistedData.customerExists,
	customerDetails: state => state.persistedData.customerDetails,
	favoriteRestaurants: state => state.persistedData.favoriteRestaurants,
	favoriteProducts: state => state.persistedData.favoriteProducts,
	customerFetchingSuccess: state => state.persistedData.customerFetchingSuccess,
	subscriptionDetails: state => state.persistedData.customerDetails && state.persistedData.customerDetails.subscriptionDetails,
	isSubscribeFlow: state => state.persistedData.isSubscribeFlow,
	focusSubscriptionTerms: state => state.persistedData.focusSubscriptionTerms,
	subscriptionTermsSubmitted: state => state.persistedData.submitSubscriptionTerms,
	isSubscriptionActive: state => {
		const { customerDetails } = state.persistedData;
		return customerDetails && customerDetails.subscriptionDetails && customerDetails.subscriptionDetails.status === 1;
	},
	subscription: state => state.persistedData.subscription,
	userCvrNumber: state => state.persistedData.userCvrNumber,
	isSubscribeSubFlow: state => state.persistedData.isSubscribeSubFlow,
	isShowDevSupport: state => state.persistedData.isShowDevSupport,
	isUserOrdersSubFlow: state => state.persistedData.isUserOrdersSubFlow,
	userOrders: state => state.userOrders,
	userLastOrder: state => state.userLastOrder,
	historyProducts: state => state.persistedData.historyProducts,
	isFeedbackSubFlow: state => state.persistedData.isFeedbackSubFlow,
	isUserDataSubFlow: state => state.persistedData.isUserDataSubFlow,
	feedbackLeft: state => state.persistedData.feedbackLeft,
	subscriptionPaymentMethod: () => "billwerk",
	isEmployee: state => state.persistedData.customerDetails
		&& state.persistedData.customerDetails.subscriptionDetails
		&& state.persistedData.customerDetails.subscriptionDetails.subscriptionPlanName.includes("Employee"),
	isSunsetPointsMember: (state, getters) => state.persistedData.customerDetails
		&& state.persistedData.customerDetails.consents
		&& state.persistedData.customerDetails.consents.acceptSunsetPointsTerms
		&& !getters.isSubscribed,
	showSunsetPointsInfo: (_, getters) => getters.isSunsetPointsMember && getters.featureToggles.enableSunsetPoints,
	forceShowingSubscriptionPlans: state => state.forceShowingSubscriptionPlans,
	sunsetPointsCount: (state, getters) => getters.isSunsetPointsMember
		&& state.persistedData.customerDetails
		&& state.persistedData.customerDetails.sunsetPoints
		&& state.persistedData.customerDetails.sunsetPoints.totalPoints,
	isSubscribed: state => state.persistedData.customerExists
		&& state.persistedData.customerDetails
		&& state.persistedData.customerDetails.subscriptionDetails
		&& ![ "draft", "ended", "cancelled", "expired"].includes(state.persistedData.customerDetails.subscriptionDetails.statusText),
	isRefreshing: state => state.auth.isRefreshing,
	refreshingCall: state => state.auth.refreshingCall,
	fraudProtection: state => state.persistedData.fraudProtection,
	idpAction: state => state.persistedData.idpAction,
	savingsStatistics: state => state.persistedData.savingsStatistics,
	subscriptionExistsData: state => state.persistedData.subscriptionExistsData,
	pushNotificationsGranted: state => state.persistedData.pushNotificationsGranted,
	recurringPaymentDetails: state => state.persistedData.recurringPaymentDetails,
	uniqueSessionId: state => state.uniqueSessionId,
	testUsers: state => state.persistedData.testUsers,
	userCrmVouchersAndMilestoneRewards: state => state.persistedData.userCrmVouchersAndMilestoneRewards,
	rcmCatalogPagesSortingData: state => state.persistedData.rcmCatalogPagesSortingData,
	subscriptionDetailsIsAlreadyDowngraded: state => state.persistedData.subscriptionDetailsIsAlreadyDowngraded,
	subscriptionUpgradePlanName: state => state.persistedData.subscriptionUpgradePlanName,
	subscriptionUpgradePackage: state => state.persistedData.subscriptionUpgradePackage,
	subscriptionMembershipPlans: state => state.persistedData.subscriptionMembershipPlans,
	subscriptionDowngradePlanName: state => state.persistedData.subscriptionDowngradePlanName,
	lastRenderedRouteSideMenu: state => state.persistedData.lastRenderedRouteSideMenu,
	usedCampaigns: state =>   state.persistedData.customerDetails
		&& state.persistedData.customerDetails.meta
		&& state.persistedData.customerDetails.meta.usedCampaigns,
	limitedDiscountAssignments: state => state.persistedData.limitedDiscountAssignments
};

// actions
const actions = {
	[actionTypes.GATHER_USER_DATA]: async ({getters, commit, dispatch}, payload = {}) => {
		const token = getters.applicationUserIdpToken;
		if (token) {
			try {
				const subscriptionExists = await SubscriptionProvider.subscriptionExists(token);
				commit(actionTypes.SET_SUBSCRIPTION_EXISTS_DATA, subscriptionExists);
				if (subscriptionExists.isEmployee) {
					await commit(actionTypes.GATHER_USER_DATA, {
						subscriptionDetails: {
							subscriptionPlanName: "SunsetPLUS Employee",
							statusText: "active"
						}
					});
					await commit(actionTypes.CUSTOMER_INITIALIZATION_REQUIRED, false);
				} else if (!(subscriptionExists && (subscriptionExists.userIsSubscriptionCustomer || subscriptionExists.isSunsetPointsMember))) {
					if(getters.featureToggles.enableSunsetPoints) {
						const { router } = await import("../../router");
						router.push("/accept-sunsetpoints-terms");
					}

					commit(actionTypes.CUSTOMER_INITIALIZATION_REQUIRED, false);
				} else {
					try {
						const details = await SubscriberDetailsProvider.getDetails(token);

						if(getters.featureToggles.enableSunsetPoints
							&& !("acceptSunsetPointsTerms" in details.consents)) {
							const { router } = await import("../../router");
							router.push("/accept-sunsetpoints-terms");
						}

						if (details.status === 404) {
							commit(actionTypes.CUSTOMER_INITIALIZATION_REQUIRED, false);
							// return {message: details.message};
						} else if (details) {
							// Note: underscores are used for versioning, so here the part starting from it is removed from the plan name/customer should not see it
							commit(actionTypes.GATHER_USER_DATA, details);
							commit(actionTypes.USER_DATA_SET, {
								email: details.email,
								name: details.fullName,
								tel: details.phone,
								isAppUser: true
							});
							if (details.subscriptionDetails) {
								commit(actionTypes.CUSTOMER_INITIALIZATION_REQUIRED, true);
							}
						}
						commit(actionTypes.CUSTOMER_FETCHING_SUCCESS, true);
					} catch (e) {
						commit(actionTypes.CUSTOMER_FETCHING_SUCCESS, false);
					}
				}
			} catch (error) {
				commit(actionTypes.CUSTOMER_FETCHING_SUCCESS, false);
			}
		}
		dispatch(actionTypes.GATHER_EVALUATION_TAG_DATA, payload);
	},
	[actionTypes.RESET_USER_ACCESS_TOKEN_USING_REFRESH_TOKEN]: async ({ commit, state, getters }) => {
		if (getters.isRefreshing) {
			return getters.refreshingCall;
		}

		if (state.persistedData.userRefreshToken) {
			commit(actionTypes.SET_REFRESHING_STATE, true);
			const refreshingCall = refreshUserToken(state.persistedData.userRefreshToken, getters.serverData.idpService).then(async res => {
				const { id_token, refresh_token, error } = res;

				if (error) {
					commit(actionTypes.REMOVE_USER_IDP_TOKEN);
					commit(actionTypes.SET_REFRESHING_STATE, false);
					commit(actionTypes.SET_REFRESHING_CALL, undefined);
					// in case of refresh token error response, user needs to re-login
					const { router } = await import("../../router");
					router.push("/check-email");
				} else {
					commit(actionTypes.SET_USER_IDP_TOKEN, id_token);
					commit(actionTypes.SET_USER_REFRESH_TOKEN, refresh_token);
					commit(actionTypes.SET_REFRESHING_STATE, false);
					commit(actionTypes.SET_REFRESHING_CALL, undefined);
				}
			});

			commit(actionTypes.SET_REFRESHING_CALL, refreshingCall);
			return refreshingCall;
		}
	},
	[actionTypes.MAXIMUM_NUMBER_OF_DEVICES_REACHING]: async ({ getters }) => {

		const res = getters.applicationUserIdpToken ? await AppUsersQueryProvider.query({idpToken: getters.applicationUserIdpToken, filters: undefined, attributes: ["activeSessions"]}) : {
			data: [
				{activeSessions: undefined}
			]
		};
		const {activeSessions} = res.data[0];
		const {maximumDevices} = getters.tenantSettings.userTrackingSessions;
		return {activeSessions, maximumDevices: +maximumDevices};
	},
	[actionTypes.ADD_FAVORITE_RESTAURANT]: async ({ getters, commit }, { shopId, isPendingOrder = false }) => {
		if (getters.applicationUserIdpToken) {
			try {
				const res = await FavoriteRestaurantProvider.addFavoriteRestaurant({
					idpToken: getters.applicationUserIdpToken,
					shopId
				});
				commit(actionTypes.SET_FAVORITE_RESTAURANTS, res.data.meta.favoriteRestaurants);
				return { favoriteRestaurants: res.data.meta.favoriteRestaurants };
			} catch (err) {
				handleMessages(err.response.data.message, commit);
			}
		} else {
			const maxLimit = getters.tenantSettings.favoriteRestaurantMaxNumberAllowed;
			if (getters.favoriteRestaurants.length >= maxLimit) {
				if (isPendingOrder) {
					return;
				}
				return handleMessages("maximum-allowed-favorite-restaurants-count-exceeded", commit);
			}
			commit(actionTypes.SET_FAVORITE_RESTAURANTS, [
				...getters.favoriteRestaurants,
				{ tenantId: appConfig.tenantId, shopId }
			]);
			return { favoriteRestaurants: getters.favoriteRestaurants };
		}
	},
	[actionTypes.DELETE_FAVORITE_RESTAURANT]: async ({ getters, commit }, { shopId }) => {
		if (getters.applicationUserIdpToken) {
			try {
				const res = await FavoriteRestaurantProvider.deleteFavoriteRestaurant({
					idpToken: getters.applicationUserIdpToken,
					shopId
				});
				commit(actionTypes.SET_FAVORITE_RESTAURANTS, res.data.meta.favoriteRestaurants);
				return { favoriteRestaurants: res.data.meta.favoriteRestaurants };
			} catch (err) {
				handleMessages(err.response.data.message, commit);
			}
		} else {
			const favoriteRestaurants = JSON.parse(JSON.stringify(getters.favoriteRestaurants));
			const idx = favoriteRestaurants.findIndex((favRes) => favRes.tenantId === appConfig.tenantId && favRes.shopId === shopId);
			if (idx !== -1) {
				favoriteRestaurants.splice(idx, 1);
			}
			commit(actionTypes.SET_FAVORITE_RESTAURANTS, favoriteRestaurants);
			return { favoriteRestaurants };
		}
	},
	[actionTypes.ADD_FAVORITE_PRODUCT]: async ({ getters, commit }, { productId, productTenantId }) => {
		if (getters.applicationUserIdpToken) {
			try {
				const res = await FavoriteProductProvider.addFavoriteProduct({
					idpToken: getters.applicationUserIdpToken,
					productId,
					productTenantId
				});
				commit(actionTypes.SET_FAVORITE_PRODUCTS, res.data.meta.favoriteProducts);
				return { favoriteProducts: res.data.meta.favoriteProducts };
			} catch (err) {
				handleMessages(err.response.data.message, commit);
			}
		}
	},
	[actionTypes.REMOVE_FAVORITE_PRODUCT]: async ({ getters, commit }, {productId, productTenantId}) => {
		if (getters.applicationUserIdpToken) {
			try {
				const res = await FavoriteProductProvider.deleteFavoriteProduct({
					idpToken: getters.applicationUserIdpToken,
					productId,
					productTenantId
				});
				commit(actionTypes.SET_FAVORITE_PRODUCTS, res.data.meta.favoriteProducts);
				return {favoriteProducts: res.data.meta.favoriteProducts};
			} catch (err) {
				handleMessages(err.response.data.message, commit);
			}
		}
	},
	[actionTypes.GET_USER_ORDERS]: async ({ getters, commit }, payload) => {
		const token = getters.applicationUserIdpToken;
		if (token) {
			try {
				const limit = payload && payload.limit || getters.tenantSettings.timers.getUserOrdersForLastMonths;
				const deliveryMethods = payload && payload.deliveryMethods;
				const res = await AppUsersProvider.getOrdersOfLastYear(token, limit, deliveryMethods);
				commit(actionTypes.SET_USER_ORDERS, res.data);
			} catch (err) {
				handleMessages("user-orders-loading-error", commit);
			}
		}
	},
	[actionTypes.GET_LAST_ORDER]: async ({ getters, commit }) => {
		const token = getters.applicationUserIdpToken;
		if (token) {
			try {
				const res = await AppUsersProvider.getLastOrder(token);
				commit(actionTypes.SET_LAST_ORDER, res.data);
			} catch (err) {
				handleMessages("user-orders-loading-error", commit);
			}
		}
	},
	[actionTypes.GET_TEST_USERS]: async ({ commit }) => {
		try {
			const res = await AppUsersProvider.getTestUsers();
			commit(actionTypes.SET_TEST_USERS, res.data);
		} catch (err) {
			handleMessages("test-users-loading-error", commit);
		}
	},
	[actionTypes.FETCH_USER_CRM_VOUCHERS_AND_MILESTONE_REWARDS]: async ({ getters, commit }) => {
		if (!getters.featureToggles.enableDiscountsPage || !getters.applicationUserLoggedIn) return;
		let membershipType, milestoneIntervalAchieved;
		if (getters.subscriptionDetails) {
			const {subscriptionPlanName, meta} = getters.subscriptionDetails;
			membershipType = (subscriptionPlanName || "sunsetplus ").toLowerCase().split("sunsetplus ")[1];
			milestoneIntervalAchieved = ((meta || {}).subscription || {}).consecutiveMonthChargesNumber;
		}
		const data = await VouchersProvider.getUserVouchers({membershipType, milestoneIntervalAchieved});
		commit(actionTypes.SET_USER_CRM_VOUCHERS_AND_MILESTONE_REWARDS, data.vouchers);
	},
	[actionTypes.FETCH_RCM_CATALOG_PAGES_SORTING]: async ({commit, getters}) => {
		const customerType = evaluateCustomerType();
		const data = {customerType, shopId: getters.globalSelectedShop.id, versionId: getters.activeVersion};
		RecommendationEngineProvider.sortCatalogProducts({data}).then(res => commit(actionTypes.SET_RCM_CATALOG_PAGES_SORTING_DATA, res));
	},
	[actionTypes.FETCH_LIMITED_DISCOUNT_ASSIGNMENTS]: async ({commit}, payload) => {
		const limitedDiscountAssignments = await LimitedDiscountAssignmentsApi.listByClientTraceId({
			tenantId: payload.tenantId,
			clientTraceId: payload.clientTraceId
		});
		commit(actionTypes.SET_LIMITED_DISCOUNT_ASSIGNMENTS, limitedDiscountAssignments);
	}
};

const handleMessages = (message, commit) => {
	switch (message) {
	case "favorite-restaurants-feature-is-disabled":
		commit(
			mutationTypes.SET_GLOBAL_INFO_DIALOG,
			"shopComponents.defaultGlobalTemplate.favoriteRestaurant.error.featureDisabled"
		);
		break;
	case "maximum-allowed-favorite-restaurants-count-exceeded":
		commit(
			mutationTypes.SET_GLOBAL_INFO_DIALOG,
			"shopComponents.defaultGlobalTemplate.favoriteRestaurant.error.maximumAllowedCountExceeded"
		);
		break;
	case "this-restaurant-is-already-favorite":
		commit(
			mutationTypes.SET_GLOBAL_INFO_DIALOG,
			"shopComponents.defaultGlobalTemplate.favoriteRestaurant.error.restaurantIsAlreadyFavorite"
		);
		break;
	case "user-orders-loading-error":
		commit(
			mutationTypes.SET_GLOBAL_INFO_DIALOG,
			"shopComponents.defaultGlobalTemplate.userOrders.error.userOrdersLoadingError"
		);
		break;
	case "product-with-given-id-does-not-exist":
		commit(
			mutationTypes.SET_GLOBAL_INFO_DIALOG,
			"shopComponents.defaultGlobalTemplate.favouriteProduct.errors.notExist"
		);
		break;
	case "maximum-allowed-favorite-products-count-exceeded":
		commit(
			mutationTypes.SET_GLOBAL_INFO_DIALOG,
			"shopComponents.defaultGlobalTemplate.favouriteProduct.errors.maximumAllowedProducts"
		);
		break;
	case "this-product-is-already-favorite":
		commit(
			mutationTypes.SET_GLOBAL_INFO_DIALOG,
			"shopComponents.defaultGlobalTemplate.favouriteProduct.errors.alreadyFavourite"
		);
		break;
	case "there-is-no-favorite-products":
		commit(
			mutationTypes.SET_GLOBAL_INFO_DIALOG,
			"shopComponents.defaultGlobalTemplate.favouriteProduct.errors.favouriteProductsEmpty"
		);
		break;
	case "this-product-is-not-favorite":
		commit(
			mutationTypes.SET_GLOBAL_INFO_DIALOG,
			"shopComponents.defaultGlobalTemplate.favouriteProduct.errors.notFavouriteProduct"
		);
		break;
	case "test-users-loading-error":
		commit(
			mutationTypes.GET_TEST_USERS,
			"shopComponents.defaultGlobalTemplate.testUsers.errors.unableToLoadTestUsers"
		);
		break;
	}
};

// mutations
const mutations = {
	[actionTypes.CUSTOMER_INITIALIZATION_REQUIRED](state, status) {
		state.persistedData.customerExists = status;
	},
	[actionTypes.SET_USER_IDP_TOKEN](state, userIdpToken) {
		state.persistedData.userIdpToken = userIdpToken;
	},
	[actionTypes.SET_REFRESHING_STATE](state, isRefreshing) {
		state.auth.isRefreshing = isRefreshing;
	},
	[actionTypes.SET_REFRESHING_CALL](state, refreshingCall) {
		state.auth.refreshingCall = refreshingCall;
	},
	[actionTypes.REMOVE_USER_IDP_TOKEN](state) {
		// we need to persist recurring payment details so that if this user makes order as guest, we still have the payment details saved
		const recurringPaymentDetails = state.persistedData.recurringPaymentDetails;
		state.persistedData = initialState();
		if (recurringPaymentDetails) {
			state.persistedData.recurringPaymentDetails = recurringPaymentDetails;
		}
	},
	[actionTypes.SET_USER_REFRESH_TOKEN](state, refreshToken) {
		state.persistedData.userRefreshToken = refreshToken;
	},
	[actionTypes.CUSTOMER_FETCHING_SUCCESS](state, customerFetchingSuccess) {
		state.persistedData.customerFetchingSuccess = customerFetchingSuccess;
	},
	[actionTypes.GATHER_USER_DATA](state, customerDetails) {
		state.persistedData.customerDetails = customerDetails;
		if(state.persistedData.customerDetails.subscriptionDetails) {
			state.persistedData.customerDetails.subscriptionDetails.meta = state.persistedData.customerDetails.meta;
		}
		state.persistedData.favoriteRestaurants = state.persistedData.customerDetails.meta.favoriteRestaurants || [];
		state.persistedData.favoriteProducts = state.persistedData.customerDetails.meta.favoriteProducts || [];
	},
	[actionTypes.SET_FAVORITE_RESTAURANTS](state, favoriteRestaurants) {
		state.persistedData.favoriteRestaurants = favoriteRestaurants;
	},
	[actionTypes.SET_FAVORITE_PRODUCTS](state, favoriteProducts) {
		state.persistedData.favoriteProducts = favoriteProducts;
	},
	[actionTypes.SET_HISTORY_PRODUCTS](state, products = []) {
		state.persistedData.historyProducts = products;
	},
	[actionTypes.SET_SUBSCRIPTION_FLOW](state, status) {
		state.persistedData.isSubscribeFlow = status;
	},
	[actionTypes.FOCUS_SUBSCRIPTION_TERMS](state, status) {
		state.persistedData.focusSubscriptionTerms = status;
	},
	[actionTypes.SUBMIT_SUBSCRIPTION_TERMS](state, status) {
		state.persistedData.submitSubscriptionTerms = status;
	},
	[actionTypes.SET_SUBSCRIPTION_DATA](state, subscriptionData) {
		state.persistedData.subscription.id = subscriptionData.id;
		state.persistedData.subscription.type = subscriptionData.type;
		state.persistedData.subscription.requiresCVR = subscriptionData.requiresCVR;
	},
	[actionTypes.SET_USER_CVR_NUMBER](state, cvrNumber) {
		state.persistedData.userCvrNumber = cvrNumber;
	},
	[actionTypes.SHOW_SUBSCRIPTION_SUB_FLOW](state, status) {
		state.persistedData.isSubscribeSubFlow = status;
	},
	[actionTypes.SHOW_DEV_SUPPORT](state, status) {
		state.persistedData.isShowDevSupport = status;
	},
	[actionTypes.SHOW_USER_ORDERS_SUB_FLOW](state, status) {
		state.persistedData.isUserOrdersSubFlow = status;
	},
	[actionTypes.SET_USER_ORDERS](state, status) {
		state.userOrders = status;
	},
	[actionTypes.SET_LAST_ORDER](state, data) {
		state.userLastOrder = data;
	},
	[actionTypes.SHOW_FEEDBACK_SUB_FLOW](state, status) {
		state.persistedData.isFeedbackSubFlow = status;
	},
	[actionTypes.SHOW_USER_DATA_SUB_FLOW](state, status) {
		state.persistedData.isUserDataSubFlow = status;
	},
	[actionTypes.FEEDBACK_LEFT](state, status) {
		state.persistedData.feedbackLeft = status;
	},
	[actionTypes.SUBSCRIPTION_PAYMENT_METHOD](state, method) {
		state.persistedData.subscriptionPaymentMethod = method;
	},
	[actionTypes.SET_FRAUD_PROTECTION](state, fraudProtection) {
		state.persistedData.fraudProtection = {...state.persistedData.fraudProtection, ...fraudProtection};
	},
	[actionTypes.HIDE_FRAUD_PROTECTION_MODAL](state) {
		state.persistedData.fraudProtection.modalVisible = false;
	},
	[actionTypes.SET_IDP_ACTION](state, value) {
		state.persistedData.idpAction = value;
	},
	[actionTypes.SET_SAVINGS_STATISTICS_VALUES](state, values) {
		state.persistedData.savingsStatistics = {
			...state.persistedData.savingsStatistics,
			...values
		};
	},
	[actionTypes.SET_SUBSCRIPTION_EXISTS_DATA](state, values) {
		state.persistedData.subscriptionExistsData = { ...values };
	},
	[actionTypes.PUSH_NOTIFICATIONS_GRANTED](state, value) {
		state.persistedData.pushNotificationsGranted = value;
	},
	[actionTypes.SET_USER_META_FRIEND_INVITE_CODE](state, value) {
		state.persistedData.customerDetails.meta.friendInviteCode = value;
	},
	[actionTypes.SET_USER_META_USED_CAMPAIGNS](state, value) {
		state.persistedData.customerDetails.meta.usedCampaigns = value;
	},
	[actionTypes.SET_RECURRING_PAYMENT_DETAILS](state, payload) {
		state.persistedData.recurringPaymentDetails = payload;
	},
	[actionTypes.SET_UNIQUE_SESSION_ID](state, payload) {
		state.uniqueSessionId = payload;
	},
	[actionTypes.SET_TEST_USERS](state, payload) {
		state.persistedData.testUsers = payload;
	},
	[actionTypes.SET_USER_CRM_VOUCHERS_AND_MILESTONE_REWARDS] (state, payload) {
		state.persistedData.userCrmVouchersAndMilestoneRewards = payload;
	},
	[actionTypes.SET_RCM_CATALOG_PAGES_SORTING_DATA](state, data) {
		state.persistedData.rcmCatalogPagesSortingData = data;
	},
	[actionTypes.SET_SUBSCRIPTION_DETAILS_IS_ALREADY_DOWNGRADED](state, payload) {
		state.persistedData.subscriptionDetailsIsAlreadyDowngraded = payload;
	},
	[actionTypes.SET_SUBSCRIPTION_UPGRADE_PLAN_NAME](state, payload) {
		state.persistedData.subscriptionUpgradePlanName = payload;
	},
	[actionTypes.SET_SUBSCRIPTION_UPGRADE_PACKAGE](state, payload) {
		state.persistedData.subscriptionUpgradePackage = payload;
	},
	[actionTypes.SET_MEMBERSHIP_PLANS](state, payload) {
		state.persistedData.subscriptionMembershipPlans = payload;
	},
	[actionTypes.SET_SUBSCRIPTION_DOWNGRADE_PLAN_NAME](state, payload) {
		state.persistedData.subscriptionDowngradePlanName = payload;
	},
	[actionTypes.FORCE_SHOW_SUBSRIPTION_PLANS](state, payload) {
		state.forceShowingSubscriptionPlans = payload;
	},
	[actionTypes.SET_LAST_RENDERED_ROUTE_SIDE_MENU](state, payload) {
		state.persistedData.lastRenderedRouteSideMenu = payload;
	},
	[actionTypes.SET_LIMITED_DISCOUNT_ASSIGNMENTS](state, payload) {
		state.persistedData.limitedDiscountAssignments = payload;
	}
};

export default {
	state,
	getters,
	actions,
	mutations
};
